import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { VendorProduct, Vendor, VendorApplication } from '../types'
import { mockVendors } from '../data/mockVendors'
import VendorProductGrid from '../components/VendorProductGrid'
import VendorSubscriptionPlans from '../components/VendorSubscriptionPlans'
import StaffProductManager from '../components/StaffProductManager'

interface VendorStats {
  totalProducts: number
  activeProducts: number
  totalViews: number
  totalSales: number
  monthlyRevenue: number
  reviewCount: number
  averageRating: number
  featuredProducts: number
  subscriptionPlan: string
  planLimits: {
    maxProducts: number | 'unlimited'
    maxFeatured: number
    usedProducts: number
    usedFeatured: number
  }
}

export default function VendorDashboard() {
  const { user, isLoggedIn } = useAuth()
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'analytics' | 'subscription' | 'profile' | 'applications'>('overview')
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [products, setProducts] = useState<VendorProduct[]>([])
  const [stats, setStats] = useState<VendorStats | null>(null)
  const [applications, setApplications] = useState<VendorApplication[]>([])
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isLoggedIn && user) {
      // Load applications (existing functionality)
      const savedApplications = JSON.parse(localStorage.getItem('vendor-applications') || '[]')
      const userApplications = savedApplications.filter((app: VendorApplication) => app.userId === user.id)
      setApplications(userApplications)

      // If user is verified vendor, load vendor data
      if (user.role === 'verified_vendor') {
        const vendorData = mockVendors.find(v => v.id === 'vendor-1') // Mock data
        setVendor(vendorData || null)
        
        // Mock products for this vendor
        const mockProducts: VendorProduct[] = [
          {
            id: 'product-1',
            name: 'Premium Red Maeng Da',
            description: 'High-quality Red Maeng Da kratom powder sourced directly from mature trees in Indonesia.',
            extractType: 'Plain Leaf',
            type: 'powder',
            sizes: [
              { weight: '1oz', price: 12.99, inStock: true },
              { weight: '4oz', price: 45.99, inStock: true },
              { weight: '8oz', price: 79.99, inStock: false }
            ],
            images: ['/api/placeholder/400/300', '/api/placeholder/400/300'],
            inStock: true,
            featured: true,
            categories: ['Plain Leaf', 'Red Strains'],
            tags: ['premium', 'mature-trees', 'indonesia'],
            dateAdded: new Date('2024-01-15'),
            salesCount: 89,
            rating: 4.6,
            reviewCount: 12
          },
          {
            id: 'product-2',
            name: '7OH Tablets - Premium',
            description: 'Precisely dosed 7-OH mitragynine tablets for consistent effects.',
            extractType: '7-OH Mitragynine',
            type: 'tablet',
            sizes: [
              { weight: '10 tablets', price: 24.99, inStock: true },
              { weight: '25 tablets', price: 54.99, inStock: true }
            ],
            images: ['/api/placeholder/400/300'],
            inStock: true,
            featured: false,
            categories: ['7OH Products', 'Tablets'],
            tags: ['lab-tested', 'consistent-dosing'],
            dateAdded: new Date('2024-01-20'),
            salesCount: 156,
            rating: 4.8,
            reviewCount: 23
          }
        ]
        setProducts(mockProducts)

        // Mock stats
        setStats({
          totalProducts: mockProducts.length,
          activeProducts: mockProducts.filter(p => p.inStock).length,
          totalViews: 2847,
          totalSales: 245,
          monthlyRevenue: 3249.56,
          reviewCount: 35,
          averageRating: 4.7,
          featuredProducts: mockProducts.filter(p => p.featured).length,
          subscriptionPlan: 'professional',
          planLimits: {
            maxProducts: 50,
            maxFeatured: 3,
            usedProducts: mockProducts.length,
            usedFeatured: mockProducts.filter(p => p.featured).length
          }
        })
      }
    }
    setLoading(false)
  }, [isLoggedIn, user])

  const handleAddProduct = (product: VendorProduct) => {
    setProducts(prev => [...prev, product])
    setShowAddProduct(false)
    // Update stats
    if (stats) {
      setStats({
        ...stats,
        totalProducts: stats.totalProducts + 1,
        planLimits: {
          ...stats.planLimits,
          usedProducts: stats.planLimits.usedProducts + 1
        }
      })
    }
  }

  const canAddProduct = () => {
    if (!stats) return false
    if (stats.planLimits.maxProducts === 'unlimited') return true
    return stats.planLimits.usedProducts < (stats.planLimits.maxProducts as number)
  }

  const canAddFeatured = () => {
    if (!stats) return false
    return stats.planLimits.usedFeatured < stats.planLimits.maxFeatured
  }

  const getStatusColor = (status: VendorApplication['status']) => {
    switch (status) {
      case 'pending': return '#f6ad55'
      case 'under_review': return '#4299e1'
      case 'approved': return '#48bb78'
      case 'rejected': return '#f56565'
      case 'needs_info': return '#ed8936'
      default: return '#718096'
    }
  }

  const getStatusText = (status: VendorApplication['status']) => {
    switch (status) {
      case 'pending': return 'Pending Review'
      case 'under_review': return 'Under Review'
      case 'approved': return 'Approved'
      case 'rejected': return 'Rejected'
      case 'needs_info': return 'Additional Info Needed'
      default: return 'Unknown'
    }
  }

  if (!isLoggedIn) {
    return (
      <div className="vendor-dashboard">
        <div className="dashboard-header">
          <h1>Vendor Dashboard</h1>
          <p>Please log in to view your vendor applications and dashboard.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="vendor-dashboard">
        <div className="dashboard-header">
          <h1>Vendor Dashboard</h1>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  // If user is verified vendor, show full dashboard
  if (user?.role === 'verified_vendor' && vendor && stats) {
    const tabs = [
      { id: 'overview', label: 'Overview', icon: '📊' },
      { id: 'products', label: 'Products', icon: '📦' },
      { id: 'analytics', label: 'Analytics', icon: '📈' },
      { id: 'subscription', label: 'Subscription', icon: '💳' },
      { id: 'profile', label: 'Profile', icon: '⚙️' },
      { id: 'applications', label: 'Applications', icon: '📝' }
    ]

    return (
      <div className="vendor-dashboard verified">
        <div className="dashboard-header">
          <div className="vendor-info">
            <h1>{vendor.name} Dashboard</h1>
            <div className="vendor-status">
              <span className="verified-badge">✓ Verified Vendor</span>
              <span className="plan-badge">{stats.subscriptionPlan} Plan</span>
            </div>
          </div>
          <div className="quick-actions">
            <button 
              className="quick-action-btn add-product"
              onClick={() => setShowAddProduct(true)}
              disabled={!canAddProduct()}
            >
              + Add Product
            </button>
            <button className="quick-action-btn view-profile">
              View Public Profile
            </button>
          </div>
        </div>

        <div className="dashboard-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="dashboard-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              
              {/* Key Metrics */}
              <div className="metrics-grid">
                <div className="metric-card revenue">
                  <div className="metric-icon">💰</div>
                  <div className="metric-info">
                    <div className="metric-value">${stats.monthlyRevenue.toLocaleString()}</div>
                    <div className="metric-label">Monthly Revenue</div>
                  </div>
                </div>
                
                <div className="metric-card sales">
                  <div className="metric-icon">🛒</div>
                  <div className="metric-info">
                    <div className="metric-value">{stats.totalSales}</div>
                    <div className="metric-label">Total Sales</div>
                  </div>
                </div>
                
                <div className="metric-card views">
                  <div className="metric-icon">👁️</div>
                  <div className="metric-info">
                    <div className="metric-value">{stats.totalViews.toLocaleString()}</div>
                    <div className="metric-label">Profile Views</div>
                  </div>
                </div>
                
                <div className="metric-card rating">
                  <div className="metric-icon">⭐</div>
                  <div className="metric-info">
                    <div className="metric-value">{stats.averageRating}</div>
                    <div className="metric-label">Avg Rating ({stats.reviewCount} reviews)</div>
                  </div>
                </div>
              </div>

              {/* Plan Usage */}
              <div className="plan-usage-section">
                <h3>Plan Usage</h3>
                <div className="usage-cards">
                  <div className="usage-card">
                    <div className="usage-header">
                      <span className="usage-label">Products</span>
                      <span className="usage-count">
                        {stats.planLimits.usedProducts} / {stats.planLimits.maxProducts === 'unlimited' ? '∞' : stats.planLimits.maxProducts}
                      </span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className="usage-fill"
                        style={{ 
                          width: stats.planLimits.maxProducts === 'unlimited' 
                            ? '20%' 
                            : `${(stats.planLimits.usedProducts / (stats.planLimits.maxProducts as number)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="usage-card">
                    <div className="usage-header">
                      <span className="usage-label">Featured Products</span>
                      <span className="usage-count">
                        {stats.planLimits.usedFeatured} / {stats.planLimits.maxFeatured}
                      </span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className="usage-fill featured"
                        style={{ width: `${(stats.planLimits.usedFeatured / stats.planLimits.maxFeatured) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {(!canAddProduct() || !canAddFeatured()) && (
                  <div className="upgrade-prompt">
                    <p>You've reached your plan limits. Consider upgrading for more products and features.</p>
                    <button 
                      className="upgrade-btn"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      View Upgrade Options
                    </button>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="recent-activity">
                <h3>Recent Activity</h3>
                <div className="activity-list">
                  <div className="activity-item">
                    <div className="activity-icon">📦</div>
                    <div className="activity-content">
                      <div className="activity-text">New product "7OH Tablets - Premium" added</div>
                      <div className="activity-time">2 hours ago</div>
                    </div>
                  </div>
                  <div className="activity-item">
                    <div className="activity-icon">⭐</div>
                    <div className="activity-content">
                      <div className="activity-text">Received 5-star review for "Premium Red Maeng Da"</div>
                      <div className="activity-time">1 day ago</div>
                    </div>
                  </div>
                  <div className="activity-item">
                    <div className="activity-icon">🛒</div>
                    <div className="activity-content">
                      <div className="activity-text">12 new sales this week</div>
                      <div className="activity-time">3 days ago</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="products-content">
              <div className="products-header">
                <h3>Your Products ({products.length})</h3>
                <div className="products-actions">
                  <button 
                    className="add-product-btn"
                    onClick={() => setShowAddProduct(true)}
                    disabled={!canAddProduct()}
                  >
                    Add New Product
                  </button>
                  {!canAddProduct() && (
                    <span className="limit-warning">Product limit reached</span>
                  )}
                </div>
              </div>
              
              <VendorProductGrid 
                vendorId={vendor.id}
                products={products}
                showAddButton={canAddProduct()}
                onAddProduct={() => setShowAddProduct(true)}
              />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="analytics-content">
              <h3>Analytics & Insights</h3>
              
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h4>Sales Performance</h4>
                  <div className="chart-placeholder">
                    <p>Sales chart would go here</p>
                    <p>Monthly sales: {stats.totalSales}</p>
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>Popular Products</h4>
                  <div className="popular-products">
                    {products
                      .sort((a, b) => b.salesCount - a.salesCount)
                      .slice(0, 3)
                      .map(product => (
                      <div key={product.id} className="popular-product">
                        <span className="product-name">{product.name}</span>
                        <span className="sales-count">{product.salesCount} sales</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="analytics-card">
                  <h4>Customer Reviews</h4>
                  <div className="review-stats">
                    <div className="review-stat">
                      <span className="review-label">Average Rating:</span>
                      <span className="review-value">{stats.averageRating}/5</span>
                    </div>
                    <div className="review-stat">
                      <span className="review-label">Total Reviews:</span>
                      <span className="review-value">{stats.reviewCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="subscription-content">
              <VendorSubscriptionPlans 
                currentPlan={stats.subscriptionPlan}
                onSelectPlan={(planId) => console.log('Selected plan:', planId)}
              />
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-content">
              <h3>Vendor Profile Settings</h3>
              <div className="profile-form">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  <div className="form-group">
                    <label>Business Name</label>
                    <input type="text" value={vendor.name} readOnly />
                  </div>
                  <div className="form-group">
                    <label>Website</label>
                    <input type="url" value={vendor.website} readOnly />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={vendor.description} readOnly rows={4} />
                  </div>
                </div>
                
                <div className="form-section">
                  <h4>Contact Information</h4>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" value={vendor.location} readOnly />
                  </div>
                  <div className="form-group">
                    <label>Shipping Regions</label>
                    <input type="text" value={vendor.shippingRegions.join(', ')} readOnly />
                  </div>
                </div>
                
                <div className="edit-notice">
                  <p>To edit your profile information, please contact our support team.</p>
                  <button className="contact-support-btn">Contact Support</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="applications-content">
              <h3>Verification Applications</h3>
              {applications.length === 0 ? (
                <div className="no-applications">
                  <p>No verification applications found.</p>
                </div>
              ) : (
                <div className="applications-list">
                  {applications.map(application => (
                    <div key={application.id} className="application-card">
                      <div className="application-header">
                        <div className="application-info">
                          <h4>{application.businessName}</h4>
                          <p className="application-date">
                            Applied on {new Date(application.applicationDate).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className="application-status">
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(application.status) }}
                          >
                            {getStatusText(application.status)}
                          </span>
                        </div>
                      </div>

                      {application.reviewNotes && (
                        <div className="review-notes">
                          <h5>Review Notes:</h5>
                          <p>{application.reviewNotes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Product Modal */}
        {showAddProduct && (
          <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add New Product</h3>
                <button className="close-btn" onClick={() => setShowAddProduct(false)}>×</button>
              </div>
              <div className="modal-body">
                <StaffProductManager onProductAdded={handleAddProduct} />
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
            <div className="modal-content large" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Upgrade Your Plan</h3>
                <button className="close-btn" onClick={() => setShowUpgradeModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <VendorSubscriptionPlans 
                  currentPlan={stats.subscriptionPlan}
                  onSelectPlan={(planId) => {
                    console.log('Upgrading to:', planId)
                    setShowUpgradeModal(false)
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // If user is not verified vendor, show application status
  return (
    <div className="vendor-dashboard">
      <div className="dashboard-header">
        <h1>Vendor Dashboard</h1>
        <p>Welcome back, {user?.username}! Manage your vendor applications and verification status here.</p>
      </div>

      <div className="dashboard-content">
        {applications.length === 0 ? (
          <div className="no-applications">
            <div className="no-applications-content">
              <h2>No Applications Found</h2>
              <p>You haven't submitted any vendor verification applications yet.</p>
              <p>Ready to get verified? Click "Apply for Verification" in the sidebar to get started!</p>
              
              <div className="verification-benefits">
                <h3>Benefits of Vendor Verification:</h3>
                <ul>
                  <li>✅ Verified badge on your vendor profile</li>
                  <li>🔝 Higher placement in search results</li>
                  <li>📢 Ability to post announcements and sales</li>
                  <li>💬 Direct response to customer reviews</li>
                  <li>📊 Access to analytics and insights</li>
                  <li>🤝 Priority customer support</li>
                  <li>📦 Product listing management</li>
                  <li>💳 Subscription plan options</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="applications-list">
            <h2>Your Applications</h2>
            
            {applications.map(application => (
              <div key={application.id} className="application-card">
                <div className="application-header">
                  <div className="application-info">
                    <h3>{application.businessName}</h3>
                    <p className="application-date">
                      Applied on {new Date(application.applicationDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="application-status">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(application.status) }}
                    >
                      {getStatusText(application.status)}
                    </span>
                  </div>
                </div>

                <div className="application-details">
                  <div className="detail-row">
                    <span className="detail-label">Website:</span>
                    <a href={application.websiteUrl} target="_blank" rel="noopener noreferrer" className="detail-value link">
                      {application.websiteUrl}
                    </a>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Business Type:</span>
                    <span className="detail-value">{application.businessType}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Products:</span>
                    <span className="detail-value">{application.productTypes.join(', ')}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Last Updated:</span>
                    <span className="detail-value">
                      {new Date(application.lastUpdated).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {application.reviewNotes && (
                  <div className="review-notes">
                    <h4>Review Notes:</h4>
                    <p>{application.reviewNotes}</p>
                    {application.reviewedBy && (
                      <p className="reviewer">- Reviewed by {application.reviewedBy}</p>
                    )}
                  </div>
                )}

                {application.status === 'approved' && (
                  <div className="approved-actions">
                    <div className="success-message">
                      <h4>🎉 Congratulations! Your application has been approved!</h4>
                      <p>Your vendor verification is now active. You can now:</p>
                      <ul>
                        <li>Update your vendor profile information</li>
                        <li>Post announcements and special offers</li>
                        <li>Respond to customer reviews</li>
                        <li>Access vendor analytics</li>
                        <li>Manage your product listings</li>
                        <li>Choose subscription plans</li>
                      </ul>
                    </div>
                  </div>
                )}

                {application.status === 'needs_info' && (
                  <div className="needs-info-actions">
                    <div className="warning-message">
                      <h4>📝 Additional Information Required</h4>
                      <p>Please review the notes above and provide the requested information to continue your application process.</p>
                      <button className="update-application-btn">
                        Update Application
                      </button>
                    </div>
                  </div>
                )}

                {application.status === 'rejected' && (
                  <div className="rejected-actions">
                    <div className="error-message">
                      <h4>❌ Application Not Approved</h4>
                      <p>Unfortunately, your application was not approved at this time. Please review the feedback above.</p>
                      <p>You may submit a new application after addressing the concerns mentioned in the review notes.</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .vendor-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #f5f7fa;
          min-height: 100vh;
        }

        .vendor-dashboard.verified {
          max-width: 1400px;
        }

        .dashboard-header {
          background: #2d3748;
          border-radius: 12px;
          padding: 30px;
          margin-bottom: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .vendor-info h1 {
          color: #2c5530;
          margin: 0 0 10px 0;
          font-size: 2.2em;
        }

        .vendor-status {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .verified-badge {
          background: #4caf50;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .plan-badge {
          background: #2c5530;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .quick-actions {
          display: flex;
          gap: 15px;
        }

        .quick-action-btn {
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .quick-action-btn.add-product {
          background: #2c5530;
          color: white;
        }

        .quick-action-btn.add-product:hover:not(:disabled) {
          background: #1e3a21;
        }

        .quick-action-btn.add-product:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .quick-action-btn.view-profile {
          background: #2d3748;
          color: #2c5530;
          border: 2px solid #2c5530;
        }

        .quick-action-btn.view-profile:hover {
          background: #2c5530;
          color: white;
        }

        .dashboard-nav {
          background: #2d3748;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          gap: 10px;
          overflow-x: auto;
        }

        .nav-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: none;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #a0aec0;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .nav-tab:hover {
          background: #f0f0f0;
          color: #2c5530;
        }

        .nav-tab.active {
          background: #2c5530;
          color: white;
        }

        .tab-icon {
          font-size: 16px;
        }

        .dashboard-content {
          background: #2d3748;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          min-height: 600px;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .metric-card {
          background: linear-gradient(135deg, #2c5530, #4caf50);
          color: white;
          padding: 25px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .metric-card.revenue {
          background: linear-gradient(135deg, #4caf50, #66bb6a);
        }

        .metric-card.sales {
          background: linear-gradient(135deg, #2196f3, #42a5f5);
        }

        .metric-card.views {
          background: linear-gradient(135deg, #ff9800, #ffb74d);
        }

        .metric-card.rating {
          background: linear-gradient(135deg, #9c27b0, #ba68c8);
        }

        .metric-icon {
          font-size: 2.5em;
        }

        .metric-value {
          font-size: 2em;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .metric-label {
          font-size: 14px;
          opacity: 0.9;
        }

        .plan-usage-section {
          margin-bottom: 40px;
        }

        .plan-usage-section h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .usage-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .usage-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .usage-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .usage-label {
          font-weight: 600;
          color: #e2e8f0;
        }

        .usage-count {
          font-weight: bold;
          color: #2c5530;
        }

        .usage-bar {
          height: 8px;
          background: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .usage-fill {
          height: 100%;
          background: #2c5530;
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .usage-fill.featured {
          background: #ffa726;
        }

        .upgrade-prompt {
          background: #2d37483cd;
          border: 1px solid #ffeaa7;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }

        .upgrade-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 10px;
        }

        .recent-activity h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .activity-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .activity-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          padding: 15px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-icon {
          font-size: 1.5em;
          flex-shrink: 0;
        }

        .activity-text {
          font-weight: 500;
          color: #e2e8f0;
          margin-bottom: 5px;
        }

        .activity-time {
          font-size: 12px;
          color: #a0aec0;
        }

        .products-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .products-header h3 {
          color: #2c5530;
          margin: 0;
        }

        .products-actions {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .add-product-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        }

        .add-product-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .limit-warning {
          color: #f44336;
          font-size: 12px;
          font-weight: 600;
        }

        .analytics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .analytics-card {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .analytics-card h4 {
          color: #2c5530;
          margin-bottom: 15px;
        }

        .chart-placeholder {
          background: #f0f0f0;
          padding: 40px;
          text-align: center;
          border-radius: 6px;
          color: #a0aec0;
        }

        .popular-products {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .popular-product {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #2d3748;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .review-stats {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .review-stat {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #2d3748;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .review-value {
          font-weight: bold;
          color: #2c5530;
        }

        .profile-form {
          max-width: 600px;
        }

        .form-section {
          margin-bottom: 30px;
        }

        .form-section h4 {
          color: #2c5530;
          margin-bottom: 15px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #e2e8f0;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          background: #f9f9f9;
        }

        .edit-notice {
          background: #e3f2fd;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }

        .contact-support-btn {
          background: #2196f3;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 10px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: #2d3748;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-content.large {
          max-width: 1200px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 30px;
          border-bottom: 1px solid #eee;
        }

        .modal-header h3 {
          margin: 0;
          color: #2c5530;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #a0aec0;
        }

        .modal-body {
          padding: 30px;
        }

        /* Existing styles for applications */
        .dashboard-header {
          text-align: center;
          margin-bottom: 40px;
          padding: 40px 20px;
          background: linear-gradient(135deg, #2c5530 0%, #4caf50 100%);
          color: white;
          border-radius: 12px;
        }

        .dashboard-header h1 {
          margin-bottom: 10px;
          font-size: 2.5em;
        }

        .dashboard-header p {
          font-size: 1.1em;
          opacity: 0.9;
        }

        .no-applications {
          text-align: center;
          padding: 60px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          border: 2px dashed #dee2e6;
        }

        .no-applications-content h2 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .no-applications-content p {
          color: #a0aec0;
          margin-bottom: 15px;
          font-size: 1.1em;
        }

        .verification-benefits {
          max-width: 600px;
          margin: 30px auto 0;
          text-align: left;
          background: #2d3748;
          padding: 30px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .verification-benefits h3 {
          color: #2c5530;
          margin-bottom: 20px;
          text-align: center;
        }

        .verification-benefits ul {
          list-style: none;
          padding: 0;
        }

        .verification-benefits li {
          padding: 10px 0;
          border-bottom: 1px solid #f0f0f0;
          color: #e2e8f0;
        }

        .verification-benefits li:last-child {
          border-bottom: none;
        }

        .applications-list {
          max-width: 800px;
          margin: 0 auto;
        }

        .applications-list h2 {
          color: #2c5530;
          margin-bottom: 30px;
          text-align: center;
        }

        .application-card {
          background: #2d3748;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin-bottom: 30px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }

        .application-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 30px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid #dee2e6;
        }

        .application-info h3, .application-info h4 {
          color: #2c5530;
          margin: 0 0 8px 0;
          font-size: 1.4em;
        }

        .application-date {
          color: #a0aec0;
          font-size: 0.9em;
          margin: 0;
        }

        .status-badge {
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85em;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .application-details {
          padding: 25px 30px;
        }

        .detail-row {
          display: flex;
          margin-bottom: 15px;
          align-items: flex-start;
        }

        .detail-label {
          font-weight: 600;
          color: #4a5568;
          min-width: 140px;
          margin-right: 15px;
        }

        .detail-value {
          color: #2d3748;
          flex: 1;
        }

        .detail-value.link {
          color: #3182ce;
          text-decoration: none;
        }

        .detail-value.link:hover {
          text-decoration: underline;
        }

        .review-notes {
          background: #f7fafc;
          border-left: 4px solid #4299e1;
          padding: 20px 25px;
          margin: 20px 30px;
          border-radius: 0 8px 8px 0;
        }

        .review-notes h4, .review-notes h5 {
          color: #2c5530;
          margin: 0 0 10px 0;
          font-size: 1.1em;
        }

        .review-notes p {
          color: #4a5568;
          line-height: 1.6;
          margin-bottom: 10px;
        }

        .reviewer {
          font-style: italic;
          color: #718096 !important;
          font-size: 0.9em;
        }

        .approved-actions, .needs-info-actions, .rejected-actions {
          padding: 25px 30px;
          border-top: 1px solid #e2e8f0;
        }

        .success-message {
          background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
          border: 1px solid #9ae6b4;
          border-radius: 8px;
          padding: 20px;
        }

        .success-message h4 {
          color: #2f855a;
          margin: 0 0 15px 0;
        }

        .success-message p {
          color: #2f855a;
          margin-bottom: 15px;
        }

        .success-message ul {
          color: #2f855a;
          margin: 0;
          padding-left: 20px;
        }

        .success-message li {
          margin-bottom: 5px;
        }

        .warning-message {
          background: linear-gradient(135deg, #fffbf0 0%, #fed7aa 100%);
          border: 1px solid #fbbf24;
          border-radius: 8px;
          padding: 20px;
        }

        .warning-message h4 {
          color: #d97706;
          margin: 0 0 15px 0;
        }

        .warning-message p {
          color: #d97706;
          margin-bottom: 15px;
        }

        .error-message {
          background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
          border: 1px solid #f87171;
          border-radius: 8px;
          padding: 20px;
        }

        .error-message h4 {
          color: #dc2626;
          margin: 0 0 15px 0;
        }

        .error-message p {
          color: #dc2626;
          margin-bottom: 10px;
        }

        .update-application-btn {
          background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: transform 0.2s ease;
        }

        .update-application-btn:hover {
          transform: translateY(-2px);
        }

        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }
          
          .quick-actions {
            justify-content: center;
          }
          
          .metrics-grid {
            grid-template-columns: 1fr;
          }
          
          .usage-cards {
            grid-template-columns: 1fr;
          }
          
          .analytics-grid {
            grid-template-columns: 1fr;
          }
          
          .dashboard-nav {
            overflow-x: auto;
          }

          .application-header {
            flex-direction: column;
            gap: 15px;
          }

          .detail-row {
            flex-direction: column;
            gap: 5px;
          }

          .detail-label {
            min-width: auto;
            margin-right: 0;
          }
        }
      `}</style>
    </div>
  )
}